import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import { v4API } from '../../Api';
import { actionKey, dateTime, errorMessage } from './common';

const active = job => ['pending', 'running'].includes(job?.status);
const labels = { import: 'Копия legacy → V4', reset: 'Обнуление балансов V4' };
const confirmations = { import: 'import-legacy-to-v4', reset: 'reset-v4-balances' };
const storageKey = 'v4-telegram-bulk-pending';
const text = (value, limit = 128) => typeof value === 'string' || typeof value === 'number' ? String(value).slice(0, limit) : '—';
const actor = value => text(value && typeof value === 'object' ? value.userId || value.id || value._id : value);
const validReason = value => !!value.trim() && value.length <= 1024;
const money = value => typeof value === 'string' && /^-?\d+\.\d{2}$/.test(value) ? `${value} RUB` : '—';
function savedRequest() {
  try {
    const value = JSON.parse(sessionStorage.getItem(storageKey));
    return value?.key && confirmations[value.body?.type] === value.body?.confirm ? value : null;
  } catch { return null; }
}

export default function TelegramBulkActions({ onLockChange, onChanged, disabled = false }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [cancelJob, setCancelJob] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [pendingRequest, setPendingRequest] = useState(savedRequest);
  const request = useRef(pendingRequest);
  const alive = useRef(false);
  const operation = useRef(false);
  const processing = useRef(false);
  const known = useRef(false);
  const jobsRef = useRef([]);
  const callbacks = useRef({ onLockChange, onChanged });
  callbacks.current = { onLockChange, onChanged };
  const publish = useCallback(items => {
    if (!alive.current) return;
    jobsRef.current = items;
    setJobs(items);
    callbacks.current.onLockChange(!known.current || items.some(active) || !!request.current);
  }, []);
  const remember = value => {
    if (value) sessionStorage.setItem(storageKey, JSON.stringify(value));
    else sessionStorage.removeItem(storageKey);
    request.current = value;
    if (alive.current) setPendingRequest(value);
  };
  const updateJob = job => {
    if (!job?._id || !['pending', 'running', 'completed', 'canceled'].includes(job.status)) throw new Error('Некорректный ответ задания');
    publish([job, ...jobsRef.current.filter(item => item._id !== job._id)]);
    if (alive.current) callbacks.current.onChanged();
  };
  const loadJobs = useCallback(async () => {
    if (operation.current) return;
    setLoading(true);
    setError('');
    try {
      const result = await v4API.telegramBulkJobs();
      if (!alive.current) return;
      if (!Array.isArray(result?.items)) throw new Error('Некорректный список заданий');
      known.current = true;
      publish(result.items);
    } catch (err) {
      if (alive.current) { known.current = false; callbacks.current.onLockChange(true); setError(errorMessage(err)); }
    } finally { if (alive.current) setLoading(false); }
  }, [publish]);
  useEffect(() => {
    alive.current = true;
    loadJobs();
    return () => { alive.current = false; processing.current = false; };
  }, [loadJobs]);

  const batches = async initial => {
    let job = initial;
    while (alive.current && processing.current && active(job)) {
      const previous = job;
      job = await v4API.processTelegramBulkJob(job._id);
      if (!alive.current) return;
      updateJob(job);
      if (job.lastErrorCode) {
        setError(`Обработка остановлена: ${text(job.lastErrorCode)}. Проверьте состояние и продолжите явно.`);
        return;
      }
      if (active(job) && job.processed === previous.processed) {
        setError('Пакет не продвинул обработку. Автоматические повторы остановлены. Проверьте состояние перед продолжением.');
        return;
      }
    }
  };
  const run = async job => {
    if (operation.current || disabled) return;
    operation.current = true;
    processing.current = true;
    setBusy(true);
    setError('');
    try { await batches(job); }
    catch (err) { if (alive.current) setError(`${errorMessage(err)} Автоматические повторы остановлены. Обновите задания; затем можно продолжить.`); }
    finally { operation.current = false; processing.current = false; if (alive.current) setBusy(false); }
  };
  const inspect = async type => {
    if (operation.current || request.current) return;
    operation.current = true;
    setLoading(true);
    setError('');
    setPreview(null);
    try {
      const result = await v4API.telegramBulkPreview(type);
      if (!alive.current) return;
      if (result?.type !== type || result.revision == null) throw new Error('Некорректный предварительный просмотр');
      setPreview(result);
      setReason('');
      setConfirm('');
      if (result.activeJob) updateJob(result.activeJob);
    } catch (err) { if (alive.current) setError(errorMessage(err)); }
    finally { operation.current = false; if (alive.current) setLoading(false); }
  };
  const start = async () => {
    if (operation.current || disabled) return;
    if (!request.current && (!preview || confirm !== confirmations[preview.type] || !validReason(reason) || jobsRef.current.some(active))) return;
    const retrying = !!request.current;
    operation.current = true;
    processing.current = true;
    setBusy(true);
    setError('');
    let started = false;
    try {
      if (!request.current) remember({ key: actionKey(), body: { type: preview.type, reason: reason.trim(), confirm, expectedRevision: preview.revision } });
      callbacks.current.onLockChange(true);
      const job = await v4API.startTelegramBulkJob(request.current.body, request.current.key);
      if (!alive.current) return;
      updateJob(job);
      remember(null);
      publish(jobsRef.current);
      started = true;
      setDialog(false);
      setPreview(null);
      await batches(job);
    } catch (err) {
      if (!alive.current) return;
      if (!started && !retrying && [400, 422].includes(err?.response?.status)) {
        remember(null);
        publish(jobsRef.current);
        setError('Запуск отклонён: проверьте данные и исправьте запрос. Причина должна содержать не более 1024 символов.');
      } else if (!started && !retrying && err?.response?.status === 409) {
        remember(null);
        setPreview(null);
        setDialog(false);
        known.current = false;
        setError('Предпросмотр устарел или задание уже запущено. Обновите задания и выполните новый предпросмотр перед подтверждением.');
      } else setError(`${errorMessage(err)} ${started ? 'Обработка остановлена; обновите задания и продолжите явно.' : 'Результат запуска неизвестен. Сохранены исходный запрос и ключ: повторите тот же запуск, не создавайте новый.'}`);
    } finally { operation.current = false; processing.current = false; if (alive.current) setBusy(false); }
  };
  const cancel = async () => {
    if (operation.current || !validReason(cancelReason) || disabled) return;
    operation.current = true;
    setBusy(true);
    setError('');
    try {
      const job = await v4API.cancelTelegramBulkJob(cancelJob._id, { reason: cancelReason.trim() });
      if (!alive.current) return;
      updateJob(job);
      setCancelJob(null);
      setPreview(null);
    } catch (err) { if (alive.current) setError(`${errorMessage(err)} Проверьте список заданий перед повтором отмены.`); }
    finally { operation.current = false; if (alive.current) setBusy(false); }
  };
  const locked = jobs.some(active);
  const blocked = disabled || busy || loading;
  return <Paper sx={{ p: 2 }}><Stack spacing={2}>
    <Typography variant="h6" component="h3">Копия и обнуление V4</Typography>
    <Alert severity="info">Копирование добавляет только отсутствующие userId и их балансы из legacy. Существующие V4-балансы не перезаписываются, в том числе после обнуления. Обнуление затрагивает только V4-балансы; пользователи и аудит сохраняются. Legacy не изменяется.</Alert>
    <Alert severity="warning">Это копирование, а не перевод средств: исходные деньги остаются в legacy, а скопированный баланс V4 независим. Переключение трафика на V4 нужно выполнять отдельно; копирование не переключает расчёты.</Alert>
    <Alert severity="warning">Предпросмотр — оценка: данные legacy могут измениться до чтения очередного пакета. Старый общий аудит не входит в историю V4. Закрытие страницы или перезагрузка приостанавливает обработку после текущего пакета; продолжение доступно в списке заданий.</Alert>
    {locked && <Alert severity="warning">Активно задание: финансовые операции Telegram V4 временно заблокированы. Просмотр доступен. Для снятия блокировки завершите или явно отмените задание.</Alert>}
    {loading && <LinearProgress aria-label="Загрузка заданий" />}
    {error && <Alert severity="error">{error}</Alert>}
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <Button disabled={blocked || !!pendingRequest} onClick={() => inspect('import')}>Предпросмотр копирования</Button>
      <Button disabled={blocked || !!pendingRequest} onClick={() => inspect('reset')}>Предпросмотр обнуления</Button>
      <Button disabled={blocked} onClick={loadJobs}>Обновить задания</Button>
      {busy && <Button onClick={() => { processing.current = false; }}>Приостановить после пакета</Button>}
    </Stack>
    {pendingRequest && <Alert severity="warning" action={<Button disabled={blocked} onClick={() => setDialog(true)}>Повторить запуск</Button>}>Сохранён неподтверждённый ответ запуска: {labels[pendingRequest.body.type]}. Причина: {pendingRequest.body.reason}. Повтор использует тот же ключ и тот же запрос, в том числе после перезагрузки.</Alert>}
    {preview && <Stack spacing={1}>
      <Typography variant="subtitle1">Предпросмотр: {labels[preview.type]}</Typography>
      <Typography>Всего: {preview.total} · Подходят: {preview.eligible} · Уже существуют: {preview.existing} · Некорректных: {preview.invalid}</Typography>
      <Typography>Сумма: {money(preview.balance)} · Ревизия: {String(preview.revision)}</Typography>
      <Button disabled={blocked || locked || !!preview.activeJob || !known.current || !!pendingRequest} onClick={() => setDialog(true)}>Перейти к подтверждению</Button>
    </Stack>}
    <Typography variant="h6" component="h3">Последние задания</Typography>
    {!loading && !jobs.length && <Typography>Заданий нет</Typography>}
    {jobs.map(job => <Paper key={job._id} variant="outlined" sx={{ p: 2 }}><Stack spacing={1}>
      <Typography>{labels[job.type] || job.type} · {job._id} · {job.status}</Typography>
      <Typography>Обработано: {job.processed} / {job.total} · Создано: {job.created} · Пропущено: {job.skipped} · Некорректных: {job.invalid} · Обнулено: {job.reset}</Typography>
      <Typography>Сумма: {money(job.amount)}</Typography>
      <Typography>Причина: {text(job.reason, 1024)} · Инициатор: {actor(job.actor)}</Typography>
      {job.cancelReason != null && <Typography>Причина отмены: {text(job.cancelReason, 1024)} · Отменил: {actor(job.canceledBy)}</Typography>}
      <Typography variant="caption">Создано: {dateTime(job.createdAt)} · Обновлено: {dateTime(job.updatedAt)}</Typography>
      {job.lastErrorCode && <Alert severity="error">{text(job.lastErrorCode)}</Alert>}
      {Array.isArray(job.errors) && job.errors.length > 0 && <Stack spacing={1}>
        <Typography>Некорректные источники (не более 20 записей)</Typography>
        <Stack component="ul" aria-label="Некорректные источники" sx={{ pl: 3, overflowWrap: 'anywhere' }}>
          {job.errors.slice(0, 20).map((item, index) => <Typography component="li" key={index}>Источник: {text(item?.sourceId)} · Код: {text(item?.code)}</Typography>)}
        </Stack>
      </Stack>}
      {active(job) && <Stack direction="row" spacing={1}>
        <Button disabled={blocked || !!pendingRequest} onClick={() => run(job)}>Продолжить</Button>
        <Button color="warning" disabled={blocked || !!pendingRequest} onClick={() => { setCancelJob(job); setCancelReason(''); }}>Отменить задание</Button>
      </Stack>}
    </Stack></Paper>)}
    <Dialog open={dialog} fullWidth maxWidth="sm" onClose={() => { if (!busy) setDialog(false); }} aria-labelledby="bulk-confirm-title">
      <DialogTitle id="bulk-confirm-title">Подтвердить: {labels[pendingRequest?.body.type || preview?.type]}</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
        <Alert severity="warning">Это реальная операция только с V4-балансами. Legacy остаётся нетронутым. После запуска финансовые операции Telegram V4 блокируются до завершения или отмены задания.</Alert>
        {pendingRequest ? <><Typography>Причина: {pendingRequest.body.reason}</Typography><Typography>Подтверждение: {pendingRequest.body.confirm} · Ревизия: {String(pendingRequest.body.expectedRevision)}</Typography><Alert severity="warning">Повтор неизменённого запроса с сохранённым ключом.</Alert></> : <>
          <Typography>Введите: {confirmations[preview?.type]}</Typography>
          <TextField label="Причина массовой операции" required value={reason} inputProps={{ maxLength: 1024 }} helperText="Не более 1024 символов" error={reason.length > 1024} disabled={busy} onChange={event => setReason(event.target.value)} />
          <TextField label="Текст подтверждения" value={confirm} disabled={busy} onChange={event => setConfirm(event.target.value)} />
        </>}
        {error && <Alert severity="error">{error}</Alert>}
      </Stack></DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setDialog(false)}>Закрыть</Button><Button color="warning" disabled={blocked || (!pendingRequest && (!validReason(reason) || confirm !== confirmations[preview?.type] || locked))} onClick={start}>{pendingRequest ? 'Повторить тот же запуск' : 'Запустить задание'}</Button></DialogActions>
    </Dialog>
    <Dialog open={!!cancelJob} fullWidth maxWidth="sm" onClose={() => { if (!busy) setCancelJob(null); }} aria-labelledby="bulk-cancel-title">
      <DialogTitle id="bulk-cancel-title">Отменить задание без отката?</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ pt: 1 }}><Alert severity="warning">Отмена снимает блокировку, но уже выполненные пакеты СОХРАНЯЮТСЯ. Это не откат.</Alert><TextField label="Причина отмены задания" required value={cancelReason} inputProps={{ maxLength: 1024 }} helperText="Не более 1024 символов" error={cancelReason.length > 1024} disabled={busy} onChange={event => setCancelReason(event.target.value)} />{error && <Alert severity="error">{error}</Alert>}</Stack></DialogContent>
      <DialogActions><Button disabled={busy} onClick={() => setCancelJob(null)}>Закрыть</Button><Button color="warning" disabled={blocked || !validReason(cancelReason)} onClick={cancel}>Подтвердить отмену задания</Button></DialogActions>
    </Dialog>
  </Stack></Paper>;
}
